import {
  Box,
  Button,
  Flex,
  H2,
  IconButton,
  ModalContainer,
  Spinner,
  useDisclosure,
} from '@ouster/webviz-react-components';

import { useCallback, useEffect, useMemo, useState } from 'react';
import ActiveRequests from 'src/components/adminDashboard/organizationMerge/ActiveRequests';
import CompletedRequests from 'src/components/adminDashboard/organizationMerge/CompletedRequests';
import {
  ACTIVE_MERGE_STATUSES,
  COMPLETED_MERGE_STATUSES,
  MERGER_ACTION,
  ActiveMergerAction,
} from 'src/components/adminDashboard/organizationMerge/const';
import OrganizationMergeCreateWizard from 'src/components/adminDashboard/organizationMerge/OrganizationMergeCreateWizard';
import {
  MergerDetailRecord,
  MergerRecord,
} from 'src/components/adminDashboard/types';
import useChakraHook from 'src/hooks/useChakraHook';
import { useStoreActions } from 'src/reducers';

type WizardMode = 'create' | 'edit' | 'view';

export const getErrorMessage = (
  err: unknown,
  fallbackMessage: string
): string => {
  if (err instanceof Error && err.message.trim()) {
    return err.message;
  }

  if (typeof err === 'string' && err.trim()) {
    return err;
  }

  if (typeof err === 'object' && err !== null) {
    const errorPayload = err as {
      reason?: unknown;
      error?: unknown;
      message?: unknown;
      data?: {
        reason?: unknown;
        error?: unknown;
      };
    };

    const candidateMessages = [
      errorPayload.reason,
      errorPayload.data?.reason,
      errorPayload.data?.error,
      errorPayload.error,
      errorPayload.message,
    ];

    const firstValidMessage = candidateMessages.find(
      (candidate): candidate is string =>
        typeof candidate === 'string' && candidate.trim().length > 0
    );

    if (firstValidMessage) {
      return firstValidMessage;
    }
  }

  return fallbackMessage;
};

export default function OrganizationMerge() {
  const {
    getMergers,
    getMergerDetails,
    executeMerger,
    sendMergerEmail,
    deleteMerger,
    openConfirmDialog,
  } = useStoreActions((actions) => ({
    ...actions.adminDashboard,
    openConfirmDialog: actions.confirmDialog.openConfirmDialog,
  }));
  const { showToast } = useChakraHook();

  const [mergers, setMergers] = useState<MergerRecord[]>([]);
  const [isLoadingMergers, setIsLoadingMergers] = useState(true);
  const [isRefreshingMergers, setIsRefreshingMergers] = useState(false);
  const [mergersError, setMergersError] = useState<string | null>(null);
  const [wizardMode, setWizardMode] = useState<WizardMode>('create');
  const [selectedMergerDetails, setSelectedMergerDetails] =
    useState<MergerDetailRecord | null>(null);
  const [activeMergerAction, setActiveMergerAction] =
    useState<ActiveMergerAction | null>(null);

  const {
    isOpen: isCreateWizardOpen,
    onOpen: onCreateWizardOpen,
    onClose: onCreateWizardClose,
  } = useDisclosure();

  const onOpenCreateWizard = () => {
    setWizardMode('create');
    setSelectedMergerDetails(null);
    onCreateWizardOpen();
  };

  const onWizardClose = () => {
    setSelectedMergerDetails(null);
    setActiveMergerAction(null);
    onCreateWizardClose();
  };

  const onOpenExistingMerger = (mergerId: number, mode: 'view' | 'edit') => {
    setActiveMergerAction({ mergerId, action: MERGER_ACTION.OPEN });
    getMergerDetails(mergerId)
      .then((mergerDetails) => {
        setSelectedMergerDetails(mergerDetails);
        setWizardMode(mode);
        onCreateWizardOpen();
      })
      .catch((err) => {
        console.error('failed to get merger details: ', err);
        showToast({
          title: 'Failed to load merge request details.',
          description: getErrorMessage(err, 'Please try again.'),
          status: 'error',
        });
      })
      .finally(() => {
        setActiveMergerAction(null);
      });
  };

  const refreshMergers = useCallback(async () => {
    try {
      const mergerRecords = await getMergers();
      setMergers(mergerRecords);
      setMergersError(null);
    } catch (err) {
      console.error('failed to refresh mergers: ', err);
      setMergers([]);
      setMergersError(getErrorMessage(err, 'Failed to load merge requests.'));
    }
  }, [getMergers]);

  const onRefreshMergers = useCallback(async () => {
    setIsRefreshingMergers(true);
    try {
      await refreshMergers();
    } finally {
      setIsRefreshingMergers(false);
    }
  }, [refreshMergers]);

  const performMergeMerger = async (mergerRecord: MergerRecord) => {
    setActiveMergerAction({
      mergerId: mergerRecord.merger_id,
      action: MERGER_ACTION.MERGE,
    });

    try {
      await executeMerger(mergerRecord.merger_id);
      showToast({
        title: 'Merge request started.',
        description: 'The merge request is executing.',
        status: 'success',
      });

      await refreshMergers();
    } catch (err) {
      console.error('failed to merge request: ', err);
      showToast({
        title: 'Failed to update merge request.',
        description: getErrorMessage(err, 'Please try again.'),
        status: 'error',
      });
    } finally {
      setActiveMergerAction(null);
    }
  };

  const onMergeMerger = (mergerRecord: MergerRecord) => {
    openConfirmDialog({
      title: 'Execute merge request',
      body: 'This merge action is irreversible. Once started, source organizations will be merged and deactivated. Are you sure you want to continue?',
      confirmLabel: 'Merge request',
      cancelLabel: 'Cancel',
      isDestructive: true,
      onConfirm: () => performMergeMerger(mergerRecord),
    });
  };

  const onResendMergerEmail = (mergerRecord: MergerRecord) => {
    const sendMergerEmailRequest = sendMergerEmail as (
      mergerId: number
    ) => Promise<unknown>;

    setActiveMergerAction({
      mergerId: mergerRecord.merger_id,
      action: MERGER_ACTION.RESEND,
    });

    sendMergerEmailRequest(mergerRecord.merger_id)
      .then(() => {
        showToast({
          title: 'Merge emails resent.',
          description: 'Notification emails were sent to merger recipients.',
          status: 'success',
        });
      })
      .catch((err) => {
        console.error('failed to resend merge emails: ', err);
        showToast({
          title: 'Failed to resend merge emails.',
          description: getErrorMessage(err, 'Please try again.'),
          status: 'error',
        });
      })
      .finally(() => {
        setActiveMergerAction(null);
      });
  };

  const performDeleteMerger = async (mergerRecord: MergerRecord) => {
    const deleteMergerRequest = deleteMerger as (
      mergerId: number
    ) => Promise<unknown>;

    setActiveMergerAction({
      mergerId: mergerRecord.merger_id,
      action: 'delete',
    });

    try {
      await deleteMergerRequest(mergerRecord.merger_id);
      showToast({
        title: 'Merge request deleted.',
        description: 'The merge request was deleted successfully.',
        status: 'success',
      });

      await refreshMergers();
    } catch (err) {
      console.error('failed to delete merge request: ', err);
      showToast({
        title: 'Failed to delete merge request.',
        description: getErrorMessage(err, 'Please try again.'),
        status: 'error',
      });
    } finally {
      setActiveMergerAction(null);
    }
  };

  const onDeleteMerger = (mergerRecord: MergerRecord) => {
    if (mergerRecord.status === 'PENDING') {
      openConfirmDialog({
        title: 'Delete merge request',
        body: 'Are you sure you want to delete this merge request? Approval notification emails have already been sent at this stage. Deleting this request will invalidate the links in those emails.',
        confirmLabel: 'Delete merge request',
        cancelLabel: 'Cancel',
        isDestructive: true,
        onConfirm: () => performDeleteMerger(mergerRecord),
      });
      return;
    }

    performDeleteMerger(mergerRecord);
  };

  useEffect(() => {
    let isMounted = true;

    const loadMergers = async () => {
      setIsLoadingMergers(true);
      setMergersError(null);

      try {
        const mergerRecords = await getMergers();
        if (!isMounted) {
          return;
        }
        setMergers(mergerRecords);
      } catch (err) {
        console.error('failed to get mergers: ', err);
        if (!isMounted) {
          return;
        }
        setMergers([]);
        setMergersError(getErrorMessage(err, 'Failed to load merge requests.'));
      } finally {
        if (isMounted) {
          setIsLoadingMergers(false);
        }
      }
    };

    loadMergers();

    return () => {
      isMounted = false;
    };
  }, [getMergers]);

  const activeRequests = useMemo(
    () =>
      mergers.filter(({ status }) => ACTIVE_MERGE_STATUSES.includes(status)),
    [mergers]
  );

  const completedRequests = useMemo(
    () =>
      mergers.filter(({ status }) => COMPLETED_MERGE_STATUSES.includes(status)),
    [mergers]
  );

  const wizardHeaderLabel =
    wizardMode === 'create'
      ? 'New Consolidation Proposal'
      : wizardMode === 'edit'
      ? 'Edit Consolidation Proposal'
      : 'View Consolidation Proposal';

  if (isLoadingMergers) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap={4}
        h="full"
      >
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <>
      <Box
        h="full"
        p="25px 36px 0px"
        display="flex"
        flexDirection="column"
        gap={16}
      >
        <Flex gap={4} alignItems="center" justifyContent="space-between">
          <H2 fontWeight="medium" fontSize={30}>
            Organization Merge Manager
          </H2>
          <Flex gap={2} alignItems="center">
            <Button
              onClick={onRefreshMergers}
              variant="outline"
              minWidth="min-content"
              data-testid="refresh-merge-requests-button"
              leftIcon="refresh"
              disabled={isRefreshingMergers}
            >
              {isRefreshingMergers
                ? 'Refreshing Requests...'
                : 'Refresh Requests'}
            </Button>
            <Button
              onClick={onOpenCreateWizard}
              minWidth="min-content"
              leftIcon="add"
              data-testid="create-merge-request-button"
              width={240}
              mr={2}
            >
              Create Merge Request
            </Button>
          </Flex>
        </Flex>

        {mergersError ? (
          <Box color="red.300" fontSize={14}>
            {mergersError}
          </Box>
        ) : null}

        <ActiveRequests
          data={activeRequests}
          onOpenMerger={onOpenExistingMerger}
          onMergeMerger={onMergeMerger}
          onResendMergerEmail={onResendMergerEmail}
          onDeleteMerger={onDeleteMerger}
          activeMergerAction={activeMergerAction}
        />

        <CompletedRequests
          data={completedRequests}
          onOpenMerger={onOpenExistingMerger}
          activeMergerAction={activeMergerAction}
        />
      </Box>

      <ModalContainer
        isOpen={isCreateWizardOpen}
        onClose={onWizardClose}
        header={
          <Flex alignItems="center" justifyContent="space-between" gap={3}>
            <Box as="span">{wizardHeaderLabel}</Box>
            <IconButton
              size="sm"
              variant="outline"
              colorScheme="gray"
              onClick={onWizardClose}
              data-testid="close-merge-request-modal-button"
              icon="close"
            />
          </Flex>
        }
        closeable={false}
        footer={null}
        content={
          <OrganizationMergeCreateWizard
            onClose={onWizardClose}
            onMergerSaved={refreshMergers}
            mode={wizardMode}
            mergerDetails={selectedMergerDetails}
          />
        }
        data-testid="create-merge-request-modal"
      />
    </>
  );
}
